import {
  Group,
  GroupDetailsType,
  MarkAsPaidOptionsInterface,
  Member,
  MemberTransactionType
} from '@types';

import { PaymentCollectionType } from '../constants';
import { getPaymentCollectionType } from '../helper';

const FREQUENCY_ANALYTICS_MAP: Record<string, string> = {
  [PaymentCollectionType.MONTHLY]: 'monthly',
  [PaymentCollectionType.WEEKLY]: 'weekly',
  [PaymentCollectionType.ONCE]: 'once',
  [PaymentCollectionType.BY_TERM]: 'term',
  [PaymentCollectionType.CUSTOM_TERM]: 'advance term',
  [PaymentCollectionType.SESSION_BASED]: 'session based'
};

export const getGroupFrequency = (
  frequency: string,
  intervalFrequency: number
) => {
  const paymentType = getPaymentCollectionType(frequency, intervalFrequency);
  return FREQUENCY_ANALYTICS_MAP[paymentType] || frequency;
};

export const getFeeCollectionMethod = (group: GroupDetailsType) =>
  group.attendance_config?.is_attendance_based_fee_enabled
    ? 'attendance_based_fee'
    : 'fixed_fee';

const getPaymentCollectionDay = (group: GroupDetailsType) => {
  const schedule = group.payment_schedule;
  if (!schedule) return undefined;
  return (
    schedule.monthly_config?.custom_day ||
    schedule.weekly_config?.dayOfWeek ||
    undefined
  );
};

export const getGroupAnalyticsPayload = (group: GroupDetailsType) => ({
  group_name: group.name,
  member_count: group.member_count,
  fee_collection_method: getFeeCollectionMethod(group),
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency),
  group_due_date: group.next_due_at,
  group_activation_date: group.activation_date,
  group_deactivation_date: group.end_date,
  group_amount: group.amount,
  is_attendance_enabled:
    group.attendance_config?.is_attendance_tracking_enabled ||
    group.is_attendance_tracking_enabled ||
    false,
  is_split_payment_enabled: group.is_split_payment || false,
  payment_collection_day: getPaymentCollectionDay(group),
  payment_collection_start_date:
    group.start_date || group.collection_started_at,
  group_status: group.is_active ? 'active' : 'inactive',
  session_count: group.payment_schedule?.sessions_per_cycle
});

export const getGroupDetailsPayload = (group: GroupDetailsType) => {
  const {
    group_frequency: groupFrequency,
    group_due_date: groupDueDate,
    session_count: sessionCount,
    ...basePayload
  } = getGroupAnalyticsPayload(group);

  return {
    ...basePayload,
    frequency: groupFrequency,
    due_date: groupDueDate,
    ...(basePayload.is_attendance_enabled && {
      send_absence_alert:
        group.attendance_config?.is_send_attendance_alert_enabled ||
        group.is_send_attendance_alert_enabled ||
        false,
      attendance_days: group.work_days || group.attendance_config?.working_days
    }),
    ...(groupFrequency === 'session based' && { session_count: sessionCount })
  };
};

export const getGroupCardEditClickedPayload = (group: Group) => ({
  group_name: group.name,
  member_count: group.member_count,
  fee_collection_method: group.is_attendance_based_fee_enabled
    ? 'attendance_based_fee'
    : 'fixed_fee',
  frequency: getGroupFrequency(group.interval, group.intervalFrequency),
  group_deactivation_date: group.end_date,
  group_amount: group.amount,
  is_split_payment_enabled: group.is_split_payment || false,
  group_status: group.is_active ? 'active' : 'inactive'
});

export const getSharePromptPayload = (group: GroupDetailsType) => ({
  group_name: group.name,
  member_count: group.member_count,
  fee_collection_method: getFeeCollectionMethod(group),
  frequency: getGroupFrequency(group.frequency, group.interval_frequency),
  due_date: group.next_due_at,
  group_activation_date: group.activation_date,
  group_deactivation_date: group.end_date,
  group_amount: group.amount
});

export const getGroupAnalyticsSummary = (group: GroupDetailsType) => ({
  group_name: group.name,
  group_status: group.is_active ? 'active' : 'inactive',
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency),
  fee_collection_method: getFeeCollectionMethod(group)
});

export const getMemberFeeCollectionMethod = (member: Member) =>
  member.is_attendance_based_fee_enabled ? 'attendance_based_fee' : 'fixed_fee';

export const getMemberAttendance = (member: Member) =>
  member.group_member_attendance_data
    ? `${member.group_member_attendance_data.present_count}/${member.group_member_attendance_data.total_attendance}`
    : undefined;

/** Lighter-weight member context for the edit-member flow, which only has
 * individual group fields (name/frequency/interval) in scope, not a full
 * GroupDetailsType. */
export const getGroupMemberEditAnalyticsPayload = (
  member: Member,
  groupName: string,
  groupFrequency: string,
  groupIntervalFrequency: number
) => ({
  member_name: member.name,
  member_primary_phone:
    member.primary_mobile_number || member.primary_mobile || member.phone,
  group_name: groupName,
  member_status: member.is_active ? 'active' : 'inactive',
  amount: member.fee_amount || member.amount,
  frequency: getGroupFrequency(
    member.payment_schedule?.interval || groupFrequency,
    member.payment_schedule?.interval_frequency ?? groupIntervalFrequency
  ),
  next_request_date: member.next_due_at,
  start_date: member.payment_schedule?.start_date,
  end_date: member.payment_schedule?.end_date,
  fee_collection_method: getMemberFeeCollectionMethod(member),
  attendance: getMemberAttendance(member)
});

export const getGroupMemberAnalyticsPayload = (
  member: Member,
  groupDetails: GroupDetailsType
) => ({
  member_name: member.name,
  member_primary_phone:
    member.primary_mobile_number || member.primary_mobile || member.phone,
  group_name: groupDetails.name,
  member_status: member.is_active ? 'active' : 'inactive',
  amount: member.fee_amount || member.amount,
  frequency: getGroupFrequency(
    member.payment_schedule?.interval || groupDetails.frequency,
    member.payment_schedule?.interval_frequency ??
      groupDetails.interval_frequency
  ),
  next_request_date: member.next_due_at,
  start_date: member.payment_schedule?.start_date,
  end_date: member.payment_schedule?.end_date,
  fee_collection_method: getMemberFeeCollectionMethod(member),
  attendance: getMemberAttendance(member)
});

export const getGroupTransactionContext = (group: GroupDetailsType) => ({
  group_name: group.name,
  fee_collection_method: getFeeCollectionMethod(group),
  group_status: group.is_active ? 'active' : 'inactive',
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency)
});

type GroupTransactionRow = Partial<MemberTransactionType>;

/** Row-level base context - group context plus the clicked transaction's identity. */
export const getGroupTransactionRowContext = (
  group: GroupDetailsType,
  row: GroupTransactionRow
) => ({
  ...getGroupTransactionContext(group),
  member_name: row.name || '',
  transaction_id: row.id || '',
  amount: row.total_amount
});

/** Same base context, built from the Mark As Paid modal's own option shape. */
export const getGroupMarkAsPaidContext = (
  group: GroupDetailsType,
  options: MarkAsPaidOptionsInterface
) => ({
  ...getGroupTransactionContext(group),
  member_name: options.name || '',
  transaction_id: options.id || '',
  amount: options.amount
});

/** Row menu (3-dot) opened - adds status/split info to the row context. */
export const getGroupTransactionMenuContext = (
  group: GroupDetailsType,
  row: GroupTransactionRow
) => ({
  ...getGroupTransactionRowContext(group, row),
  status: row.status,
  is_split_payment: row.is_split_payment
});

/** Payment details view - adds paid/remaining amounts and requested date. */
export const getGroupTransactionPaymentDetailsContext = (
  group: GroupDetailsType,
  row: GroupTransactionRow
) => ({
  ...getGroupTransactionMenuContext(group, row),
  total_paid: row.total_paid_amount,
  remaining_amount: (
    Number(row.total_amount || 0) - Number(row.total_paid_amount || 0)
  ).toString(),
  requested_on: row.requested_on
});

/** Fee summary accordion - amount breakdown, no status/split info needed. */
export const getGroupTransactionFeeSummaryContext = (
  group: GroupDetailsType,
  row: GroupTransactionRow,
  subTotal?: string,
  totalGst?: string
) => ({
  ...getGroupTransactionContext(group),
  member_name: row.name || '',
  transaction_id: row.id || '',
  sub_total: subTotal ?? row.original_amount_requested,
  total_gst: totalGst ?? row.total_gst,
  total_amount: row.total_amount
});

export const getGroupQuickCollectContext = (
  group: GroupDetailsType,
  branchName: string
) => ({
  branch_name: branchName,
  group_name: group.name,
  group_amount: group.amount,
  fee_collection_method: getFeeCollectionMethod(group),
  group_status: group.is_active ? 'active' : 'inactive',
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency)
});
